'use client';

import { useEffect, useMemo, useState } from 'react';
import {
  SportsEventsClient,
  commonClient,
  type PaginatedEvents,
} from '@bip/api-client';
import { CONCERTS_API_BASE_URL } from '../config';
import { EventsFilters } from './EventsFilters';
import { EventsList } from './EventsList';
import { BackToTop } from './BackToTop';
import { SkeletonBox } from '../../_components/Skeleton';

/**
 * Concerts home listing — replaces the marketing hero home per spec:
 *
 *   1. `/concerts` renders  the  listing view by default.
 *   2. URL stays `/concerts` on load — no query params appended for
 *      the resolved city (only user-triggered filter changes may
 *      touch the URL, and even then `cityId` is a base filter, not
 *      a URL param).
 *   3. City is resolved on mount via a 3-priority ladder (spec
 *      2026-08-05 revised):
 *      1) `theatre-searches-city` localStorage blob (same key the
 *         Sports / Restaurants / Hotels domains use — one shared
 *         "last searched city" across the whole product).
 *      2) IP-based geolocation via the common-service ipwho proxy;
 *         accepted only when the detected name resolves to a real
 *         `cityId` in the concerts city collection.
 *      3) Hardcoded `"London"` fallback — guarantees  the  listing
 *         always renders city-scoped content.
 *   4. Resolved city name is looked up against the events catalogue
 *      via `lookupCity` to convert name → `cityId`; the id is then
 *      passed to `EventsList` as `baseFilters={{ cityId }}` so the
 *      backend filters correctly without needing the URL.
 *
 * While the resolver runs, a skeleton placeholder is shown so the
 * layout doesn't shift.
 */

/**
 * City resolution — same shape as the Sports SearchBar's landing
 * auto-populate (see `app/sports/_components/SearchBar.tsx`
 * around `theatre-searches-city`):
 *   • ONLY read the cross-domain `theatre-searches-city`
 *     localStorage blob — no IP geolocation, no hardcoded
 *     "London" fallback (sports doesn't fall back either; if the
 *     blob is empty the SearchBar just leaves the field blank).
 *   • Resolve the city name → id via `getSearchResultData` (the
 *     same endpoint sports uses), then match with the same
 *     equality → startsWith → prefix rule sports applies so a
 *     "London, UK" blob still matches the catalogue's "London"
 *     row (and vice versa).
 *   • When the blob is missing or the lookup finds no matching
 *     city, render the unfiltered catalogue rather than a blank
 *     state — sports's SearchBar path never lands on the listing
 *     without a chosen city, but on the concerts landing the
 *     listing IS the surface, so a no-city path must still show
 *     something.
 */
function readCityFromStorage(): string {
  if (typeof window === 'undefined') return '';
  try {
    const raw = localStorage.getItem('theatre-searches-city');
    if (!raw) return '';
    const parsed = JSON.parse(raw) as { city?: string } | null;
    return (parsed?.city ?? '').trim();
  } catch {
    return '';
  }
}

/**
 * Concerts-home city-resolve precedence — used by the effect below to
 * pick the city that seeds the initial  listing fetch (spec
 * 2026-08-05):
 *
 *   1. `theatre-searches-city` localStorage blob (user's last pick,
 *      shared with sports / restaurants / hotels).
 *   2. IP-based auto-detect via the common-service ipwho.org proxy
 *      (`commonClient.getGeo()` → `GET /bip-api/common/geo`) — used
 *      ONLY when the detected name resolves to a real `cityId` in
 *      the concerts city collection.
 *   3. Hardcoded fallback: `"London"` (spec 2026-08-05 revised) —
 *      guarantees the listing always renders city-scoped content
 *      when the two prior sources miss. The fallback is a DISPLAY
 *      seed only; we do NOT write `theatre-searches-city` for it
 *      (the strict-shape rule from the earlier spec still stands —
 *      only real user picks / drilldown syncs write the blob).
 *
 * Each candidate is resolved against the concerts catalogue via
 * `getSearchResultData` (same endpoint sports uses).
 */
const CONCERTS_CITY_FALLBACK = 'London';

async function resolveGeoCity(): Promise<string> {
  try {
    const resp = await commonClient.getGeo();
    return (resp?.data?.city ?? '').trim();
  } catch {
    return '';
  }
}

const EMPTY_DATA: PaginatedEvents = {
  data: [],
  total: 0,
  page: 1,
  limit: 12,
  totalPages: 0,
};

export function ConcertsHomeListing() {
  const client = useMemo(() => new SportsEventsClient(CONCERTS_API_BASE_URL), []);
  // `resolvedCityId` is the auto-detected city id we thread to
  // `EventsList` via `baseFilters` so every fetch — initial load AND
  // every filter-change re-fetch — stays scoped to the resolved
  // city, without the URL carrying `?cityId=`. It's `null` until
  // the resolver runs (or when every candidate misses the catalogue
  // and the listing renders unscoped).
  const [resolvedCityId, setResolvedCityId] = useState<number | null>(null);
  // `cityName` seeds the empty-state / entity label the listing
  // shows above the grid. The resolved `cityId` deliberately does
  // NOT sit in React state — it's written straight to the URL as
  // `?cityId=<id>` so both `EventsList.buildQueryFromUrl` and
  // `EventsFilters` (which read from the URL) stay in sync without
  // a second source of truth.
  const [cityName, setCityName] = useState<string>('');
  const [initialData, setInitialData] = useState<PaginatedEvents>(EMPTY_DATA);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const resolveAndFetch = async () => {
      // Resolve a single city NAME → catalogue `cityId` using the
      // exact/startsWith/comma-prefix match sports uses. Returns
      // `{ id, name }` on hit, `null` on catalogue miss / network
      // failure.
      const resolveCandidate = async (
        candidate: string,
      ): Promise<{ id: number; name: string } | null> => {
        const name = (candidate || '').trim();
        if (!name) return null;
        try {
          const res = await client.getSearchResultData(name);
          const lower = name.toLowerCase();
          const match = res?.cities?.find(
            (c: any) =>
              c.name?.toLowerCase() === lower ||
              c.name?.toLowerCase().startsWith(lower) ||
              lower.startsWith(c.name?.toLowerCase().split(',')[0]),
          );
          if (match?.id && Number.isFinite(match.id)) {
            return { id: Number(match.id), name: match.name || name };
          }
        } catch {
          /* fall through to caller — next candidate takes over */
        }
        return null;
      };

      // First check: did the user pick a city via the filter dropdown
      // (writes `?cityId=` to the URL) OR did they arrive via a
      // deep-link that carries `?cityId=`? If so, that WINS — the
      // filter selection must survive across the auto-resolve.
      // Without this branch, the resolver would overwrite the URL
      // pick with the localStorage / geo / London default on the
      // next render, and the City filter dropdown would look
      // ineffective.
      const initialUrlParams = new URLSearchParams(window.location.search);
      const urlCityIdRaw = initialUrlParams.get('cityId');
      let resolved: { id: number; name: string } | null = null;
      if (urlCityIdRaw) {
        const parsedUrlCityId = Number(urlCityIdRaw);
        if (Number.isFinite(parsedUrlCityId)) {
          resolved = { id: parsedUrlCityId, name: '' };
          // Deep-link entity-label backfill: `EventsFilters` fetches
          // its own filter-options list on mount and renders the
          // city chip from that; the entity label ABOVE the listing
          // reads `cityName` state, which would stay empty for a
          // pure `?cityId=<id>` URL. Removed the stale
          // `getSearchResultData('')` call that used to sit here —
          // the backend's `searchEventData` short-circuits on
          // searchStr < 3 chars, so it always returned `[]` and the
          // ".then" wasn't wired anyway. The label just stays blank
          // for URL-only cityId until `EventsFilters` renders its
          // chip (which is the source of truth for the picked city).
        }
      }

      // City-resolve precedence per spec (only runs when the URL
      // didn't carry `?cityId=` above):
      //   1. `theatre-searches-city` localStorage blob (last picked,
      //      shared across sports / restaurants / hotels).
      //   2. IP-based auto-detect via ipwho.org proxy — used ONLY
      //      when the detected name resolves to a real `cityId` in
      //      the concerts catalogue (city collection).
      //   3. Hardcoded `"London"` fallback (spec 2026-08-05 revised).
      if (!resolved) {
        const stored = readCityFromStorage();
        if (stored) {
          resolved = await resolveCandidate(stored);
          if (cancelled) return;
          if (!resolved) setCityName(stored); // display the typed name while we fall through
        }

        if (!resolved) {
          const detected = await resolveGeoCity();
          if (cancelled) return;
          if (detected) {
            resolved = await resolveCandidate(detected);
            if (cancelled) return;
            if (!resolved && !stored) setCityName(detected);
          }
        }

        if (!resolved) {
          resolved = await resolveCandidate(CONCERTS_CITY_FALLBACK);
          if (cancelled) return;
          if (!resolved) setCityName(CONCERTS_CITY_FALLBACK);
        }
      }

      if (cancelled) return;
      if (resolved) {
        if (resolved.name) setCityName(resolved.name);
        setResolvedCityId(resolved.id);
        // Home-page contract (2026-07-31, reverted the URL-mutation
        // approaches): the URL STAYS at `/concerts` — no `?cityId=`
        // appended, no `/concerts/city/<slug>-<id>` redirect. The
        // listing is silently city-scoped via `baseFilters` (below),
        // and the SearchBar auto-populates from localStorage on
        // mount, so writing the resolved name here means the SB
        // shows the resolved city as a "selected" chip whenever the
        // resolver hits a real catalogue row. The custom event
        // notifies an ALREADY-mounted SearchBar to re-populate
        // without needing a mount cycle. Only fires when the URL
        // didn't already carry `?cityId=` — a deep-link caller
        // (which we don't rewrite) still gets exactly what they
        // requested.
        if (!urlCityIdRaw && resolved.name) {
          // Cross-domain `theatre-searches-city` blob — STRICT shape
          // (spec 2026-08-05). MUST be the full
          // `{ city, country, id: destination_id, state, dest_id: _id }`
          // sourced from a real `SearchLocation` row via
          // `getCityDestId`. If the lookup fails or misses, we WRITE
          // NOTHING — never a partial blob. Downstream readers rely
          // on the full shape (hotels SearchBar keys off `dest_id`
          // for its own recent-search dedup) and a partial blob leaks
          // a broken value across domains.
          const writeCityBlob = async () => {
            try {
              const cityQuery = resolved!.name.split(',')[0].trim();
              const res = await client.getCityDestId(cityQuery);
              if (cancelled) return;
              if (res?.message !== 'success' || !res?.data) return;
              const doc = res.data as {
                city?: string;
                country?: string;
                destination_id?: string;
                state?: string;
                _id?: string;
              };
              // Every field must be present + non-empty. A row missing
              // `_id` or `destination_id` in the SearchLocation
              // collection is a CMS data gap — don't paper over it
              // with an empty string; skip the write.
              if (!doc.city || !doc._id || !doc.destination_id) return;
              const blob = {
                city: doc.city,
                country: doc.country || '',
                id: doc.destination_id,
                state: doc.state || '',
                dest_id: doc._id,
              };
              localStorage.setItem('theatre-searches-city', JSON.stringify(blob));
            } catch {
              /* CMS unreachable / localStorage disabled — don't write */
            }
          };
          void writeCityBlob();

          try {
            window.dispatchEvent(
              new CustomEvent('concertsHomeCityResolved', {
                detail: { id: resolved.id, name: resolved.name },
              }),
            );
          } catch {
            /* CustomEvent unsupported (dev-only edge) — silent */
          }
        }
      }
      // No `else` branch — when all three candidates miss the
      // catalogue we simply don't write `?cityId=`, so both the
      // listing and the filter chip render city-free. Matches the
      // previous "leave it blank" behaviour without needing a
      // dedicated state signal.

      const resolvedId: number | null = resolved?.id ?? null;

      // Merge URL filter params into the initial fetch so a refresh
      // on a filtered URL (e.g. `/concerts?venueId=5&minPrice=10`)
      // renders the FILTERED first page directly, not the
      // unfiltered city-scoped one. Without this, `EventsList` only
      // re-fetches on the `filtersChanged` event, which fires on
      // user interaction — refresh alone left the initial 12 cards
      // showing every event in the city while the filter chips /
      // dropdowns claimed a narrower selection.
      //
      // Read straight off `window.location.search` (component runs
      // client-side inside `useEffect`, so `window` is defined).
      // Mirrors the exact param set `EventsList.buildQueryFromUrl`
      // consumes so behaviour is identical between "initial refresh"
      // and "filter change".
      const sp = new URLSearchParams(window.location.search);
      const urlDateFrom = sp.get('dateFrom');
      const urlDateTo = sp.get('dateTo');
      const urlDateRange = sp.get('dateRange');
      const fmt = (d: Date) => d.toLocaleDateString('en-GB');
      const today = new Date();
      const rangeToDates = (): { dateFrom?: string; dateTo?: string } => {
        if (!urlDateRange) {
          return {
            dateFrom: urlDateFrom || undefined,
            dateTo: urlDateTo || undefined,
          };
        }
        if (urlDateRange === 'today') {
          const t = fmt(today);
          return { dateFrom: t, dateTo: t };
        }
        if (urlDateRange === 'next7days' || urlDateRange === 'thisWeek') {
          const to = new Date(today);
          to.setDate(today.getDate() + 7);
          return { dateFrom: fmt(today), dateTo: fmt(to) };
        }
        if (urlDateRange === 'thisMonth') {
          const eom = new Date(today.getFullYear(), today.getMonth() + 1, 0);
          return { dateFrom: fmt(today), dateTo: fmt(eom) };
        }
        return {
          dateFrom: urlDateFrom || undefined,
          dateTo: urlDateTo || undefined,
        };
      };
      const dateRange = rangeToDates();

      const searchParam = sp.get('search') || undefined;
      const participantId = sp.get('participantId') || undefined;
      const teamType = sp.get('teamType') || undefined;
      // Multi-select CSV — pass through as-is; the concerts backend
      // parses via `parseIdList` + `$in`. Cast to `any` because
      // the shared `SearchEventsParams.venueId` type is `number`
      // (kept lean for sports parity).
      const venueId = (sp.get('venueId') || undefined) as any;
      const minPriceParam = sp.get('minPrice');
      const maxPriceParam = sp.get('maxPrice');
      const minPrice = minPriceParam ? parseFloat(minPriceParam) : undefined;
      const maxPrice = maxPriceParam ? parseFloat(maxPriceParam) : undefined;

      // Server-authoritative first page. `cityId` is omitted when
      // resolvedId is null (no localStorage blob or catalogue miss)
      // so the listing shows the global catalogue rather than a
      // blank state — matches sports's behaviour where a missing
      // blob just leaves the SearchBar empty.
      try {
        const events = await client.searchEvents({
          ...(resolvedId != null ? { cityId: resolvedId } : {}),
          search: searchParam,
          participantId,
          teamType,
          venueId,
          minPrice,
          maxPrice,
          dateFrom: dateRange.dateFrom,
          dateTo: dateRange.dateTo,
          page: 1,
          limit: 12,
          sortBy: 'dateOfEvent',
          sortOrder: 'asc',
        });
        if (!cancelled) {
          setInitialData(events);
          setLoading(false);
        }
      } catch {
        if (!cancelled) {
          setInitialData(EMPTY_DATA);
          setLoading(false);
        }
      }
    };

    resolveAndFetch();
    return () => {
      cancelled = true;
    };
  }, [client]);

  // Skeleton — six animated placeholder cards mirroring the
  // EventCard outline (heading + venue + location + date + price
  // rows). Uses the shared `SkeletonBox` primitive so the shimmer
  // animation matches every other loading state in the app (search
  // results, detail page, checkout). The "Loading concerts…" text
  // heading was removed — the shimmer itself communicates the
  // pending state without a redundant label.
  if (loading) {
    return (
      <section className="pt-4 pb-5">
        <div className="container">
          <div className="row">
            {Array.from({ length: 6 }).map((_, i) => (
              <div className="col-lg-4 col-md-6 mb-4" key={i}>
                <div
                  style={{
                    background: '#fff',
                    borderRadius: 14,
                    border: '1px solid #eee',
                    padding: 20,
                  }}
                >
                  {/* Title */}
                  <SkeletonBox width="90%" height={22} mb={14} />
                  {/* Venue row */}
                  <SkeletonBox width="70%" height={14} mb={10} />
                  {/* Location row */}
                  <SkeletonBox width="80%" height={14} mb={10} />
                  {/* Date row */}
                  <SkeletonBox width="60%" height={14} mb={20} />
                  {/* Price + CTA row */}
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <SkeletonBox width="35%" height={20} />
                    <SkeletonBox width={90} height={32} radius={20} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  // Home page uses `pageType="city"` so the City dropdown is HIDDEN
  // (the `city` matrix excludes cityId — same matrix
  // `/concerts/city/[slug]` uses, where the route itself locks the
  // city). The resolved cityId is NOT written to the URL (URL stays
  // at `/concerts`); it rides silently on `EventsList.baseFilters`
  // instead so every fetch preserves the city scope. The SearchBar
  // shows the resolved city as a pre-selected chip via the
  // `concertsHomeCityResolved` custom event + localStorage. Every
  // other filter (participants, venue, price, date) remains
  // URL-backed and user-changeable.
  const entityName = cityName || undefined;

  return (
    <>
      <EventsFilters
        basePath="/concerts"
        pageType="city"
        total={initialData.total}
        // Same city scope the listing below uses. Without this the
        // filter-options query ran unscoped, so the price slider
        // anchored to the GLOBAL catalogue min/max while the cards
        // showed one city — a range that mostly filtered nothing.
        // `filterOptionsQueryKey` only picks `cityId` up from
        // `baseFilters` (user-applied params are deliberately
        // excluded there), so passing it here is the only way to
        // scope the range. Artist and venue counts become
        // city-scoped for the same reason, which is what
        // `/concerts/city/[slug]` already does.
        //
        // Safe despite that exclusion rule existing to stop a
        // dropdown collapsing to its own selection: `pageType="city"`
        // omits `cityId` from VISIBLE_FILTERS, so there is no city
        // dropdown here to collapse.
        baseFilters={resolvedCityId != null ? { cityId: resolvedCityId } : undefined}
        // Concerts home hides ONLY the auto-resolved city chip (the
        // city is silently resolved via localStorage → geo → London
        // fallback, and removing it would empty the listing with no
        // way back). Other chips — dates, price, artist, venue —
        // still surface the moment the user actually sets them, so
        // on first page load the chips row is empty and the whole
        // row stays hidden by the shared `chips.length > 0` gate.
        hideCityChip
      />
      <EventsList
        initialData={initialData}
        apiBaseUrl={CONCERTS_API_BASE_URL}
        entityName={entityName}
        // Silent city scope — cityId is passed via `baseFilters` so
        // every subsequent filter-change re-fetch preserves it,
        // even though the URL stays at `/concerts` with no `?cityId=`
        // query param. `null` = no auto-resolve hit → unscoped
        // catalogue, as before.
        baseFilters={resolvedCityId != null ? { cityId: resolvedCityId } : undefined}
      />
      <BackToTop />
    </>
  );
}
